import { Timer } from 'lucide-react'

export type DurationUnit = 'minutes' | 'seconds' | 'days' | 'milliseconds'

/**
 * Story 3.16.1 — Display read-only de settings numéricas que representam
 * duração. Formato auto-convertido para a unidade humana mais natural
 * (ex: 1440min → "1440 min (≈ 24h)" OR 365 days → "365 dias (≈ 12 meses)").
 */
export function DurationDisplay({
  value,
  unit,
}: {
  value: unknown
  unit: DurationUnit
}): React.ReactElement {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return <code className="text-xs text-muted-foreground">(valor inválido)</code>
  }

  const label = formatLabel(value, unit)
  const conversion = formatConversion(value, unit)

  return (
    <div className="flex items-center gap-2 text-sm">
      <Timer className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      <span>
        <span className="font-medium">{label}</span>
        {conversion !== null && (
          <span className="ml-2 text-xs text-muted-foreground">({conversion})</span>
        )}
      </span>
    </div>
  )
}

function formatLabel(value: number, unit: DurationUnit): string {
  switch (unit) {
    case 'minutes':
      return `${value} min`
    case 'seconds':
      return `${value} s`
    case 'days':
      return `${value} dia${value === 1 ? '' : 's'}`
    case 'milliseconds':
      return `${value} ms`
  }
}

function formatConversion(value: number, unit: DurationUnit): string | null {
  switch (unit) {
    case 'minutes': {
      if (value >= 60 && value < 1440) return `≈ ${(value / 60).toFixed(1)}h`
      if (value >= 1440) return `≈ ${(value / 1440).toFixed(1)} dias`
      return null
    }
    case 'days': {
      if (value >= 30 && value < 365) return `≈ ${Math.round(value / 30)} meses`
      if (value >= 365) return `≈ ${(value / 365).toFixed(1)} anos`
      return null
    }
    case 'seconds': {
      if (value >= 60) return `≈ ${(value / 60).toFixed(1)} min`
      return null
    }
    case 'milliseconds': {
      if (value >= 1000) return `≈ ${(value / 1000).toFixed(1)} s`
      return null
    }
  }
}
